import React, { useState, useRef, useCallback, useEffect } from 'react';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { RadioSetting } from '../../components/SettingCards/RadioSetting/RadioSetting';
import { Dropdown, DropdownOption } from '../../components/Dropdown/Dropdown';
import { PrimaryButton, SecondaryButton } from '../../components/Buttons/Buttons';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { DISPLAY_SETTING_IDS } from '../../config/SettingIds';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import './MonitorLayout.css';

export interface Monitor {
	id: string;
	dpi: number;
	x: number;
	y: number;
	width: number;
	height: number;
	isPrimary: boolean;
	number: number; // Display number for the monitor
}

/**
 * Data contract for monitor layout sent to/received from native bridge.
 * This is what gets serialized as the settingValue for the 'monitor-layout' setting key.
 */
export interface MonitorPartition {
	id: string;
	dpi: number;
	x: number;
	y: number;
	width: number;
	height: number;
	isPrimary: boolean;
}

export interface MonitorLayoutData {
	monitors: MonitorPartition[];
}

interface MonitorLayoutProps {
	title?: string;
	description?: string;
	learnMoreUrl?: string;
	learnMoreText?: string;
}

type PartitionMode = 'horizontal' | 'vertical';

export const MonitorLayout: React.FC<MonitorLayoutProps> = ({
	title = 'Monitor layout',
	description = 'Click on a monitor to split it. Right-click to set primary monitor.',
	learnMoreUrl,
	learnMoreText,
}) => {
	const canvasRef = useRef<HTMLDivElement>(null);
	const monitorHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const monitorDpiRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const [monitors, setMonitors] = useState<Monitor[]>([
		{
			id: 'monitor-1',
			dpi: 100,
			x: 0,
			y: 0,
			width: 100,
			height: 100,
			isPrimary: true,
			number: 1,
		},
	]);
	const [partitionMode, setPartitionMode] = useState<PartitionMode>('horizontal');
	const [hoveredMonitor, setHoveredMonitor] = useState<string | null>(null);
	const [selectedMonitor, setSelectedMonitor] = useState<string | null>(null);
	const [monitorIdCounter, setMonitorIdCounter] = useState(2);
	const [previewSplit, setPreviewSplit] = useState<{
		monitorId: string;
		position: number;
		globalPosition: number;
	} | null>(null);

	/** Convert monitors state to bridge data format (strip UI-only fields) */
	const serializeLayout = useCallback((monitorsToSerialize: Monitor[]): string => {
		const data: MonitorLayoutData = {
			monitors: monitorsToSerialize.map(
				({ id, dpi, x, y, width, height, isPrimary }) => ({
					id,
					dpi,
					x: Math.round(x * 100) / 100,
					y: Math.round(y * 100) / 100,
					width: Math.round(width * 100) / 100,
					height: Math.round(height * 100) / 100,
					isPrimary,
				})
			),
		};
		return JSON.stringify(data);
	}, []);

	/** Parse bridge data into Monitor[] state */
	const deserializeLayout = useCallback((value: string): Monitor[] | null => {
		try {
			const data: MonitorLayoutData =
				typeof value === 'string' ? JSON.parse(value) : value;
			if (
				!data?.monitors ||
				!Array.isArray(data.monitors) ||
				data.monitors.length === 0
			) {
				return null;
			}
			return data.monitors.map((m, index) => ({
				...m,
				number: index + 1,
			}));
		} catch {
			return null;
		}
	}, []);

	/** Save current layout to native bridge */
	const saveLayoutToBridge = useCallback(
		async (monitorsToSave: Monitor[]) => {
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();
				const serialized = serializeLayout(monitorsToSave);
				await bridge.setSetting({
					settingKey: DISPLAY_SETTING_IDS.MONITOR_LAYOUT,
					settingValue: serialized,
				});
			} catch (error) {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					`Failed to save monitor layout: ${error}`,
					AotCategory.Error,
					`Failed to save monitor layout: ${error}`
				);
			}
		},
		[serializeLayout]
	);

	/** Load layout from native bridge on mount */
	useEffect(() => {
		const loadLayoutFromBridge = async () => {
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();
				const response = await bridge.getSetting({
					settingKey: DISPLAY_SETTING_IDS.MONITOR_LAYOUT,
				});

				if (response?.status === 0 && response.data?.settingValue) {
					const loaded = deserializeLayout(response.data.settingValue);
					if (loaded) {
						setMonitors(loaded);
						setMonitorIdCounter(
							Math.max(
								...loaded.map((m) => {
									const num = parseInt(m.id.replace('monitor-', ''), 10);
									return isNaN(num) ? 0 : num;
								})
							) + 1
						);
					}
				}
			} catch (error) {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					`Failed to load monitor layout: ${error}`,
					AotCategory.Error,
					`Failed to load monitor layout: ${error}`
				);
			}
		};

		loadLayoutFromBridge();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dpiOptions: DropdownOption[] = [
		{ value: '100', label: '100' },
		{ value: '125', label: '125' },
		{ value: '150', label: '150' },
		{ value: '175', label: '175' },
		{ value: '200', label: '200' },
		{ value: '225', label: '225' },
		{ value: '250', label: '250' },
		{ value: '275', label: '275' },
		{ value: '300', label: '300' },
		{ value: '325', label: '325' },
		{ value: '350', label: '350' },
	];

	const findClickedMonitor = useCallback(
		(x: number, y: number): Monitor | null => {
			return (
				monitors.find(
					(monitor) =>
						x >= monitor.x &&
						x <= monitor.x + monitor.width &&
						y >= monitor.y &&
						y <= monitor.y + monitor.height
				) || null
			);
		},
		[monitors]
	);

	const splitMonitor = (
		monitor: Monitor,
		clickX: number,
		clickY: number,
		mode: PartitionMode
	): Monitor[] => {
		const relativeX = clickX - monitor.x;
		const relativeY = clickY - monitor.y;

		if (mode === 'horizontal') {
			// Horizontal partition: horizontal line dividing top and bottom
			const splitPercentage = (relativeY / monitor.height) * 100;
			const splitPoint = Math.max(10, Math.min(90, splitPercentage));

			const topMonitor: Monitor = {
				...monitor,
				id: monitor.id,
				height: (monitor.height * splitPoint) / 100,
			};

			const bottomMonitor: Monitor = {
				id: `monitor-${monitorIdCounter}`,
				dpi: 100,
				x: monitor.x,
				y: monitor.y + (monitor.height * splitPoint) / 100,
				width: monitor.width,
				height: (monitor.height * (100 - splitPoint)) / 100,
				isPrimary: false,
				number: monitorIdCounter,
			};

			return [topMonitor, bottomMonitor];
		} else {
			// Vertical partition: vertical line dividing left and right
			const splitPercentage = (relativeX / monitor.width) * 100;
			const splitPoint = Math.max(10, Math.min(90, splitPercentage));

			const leftMonitor: Monitor = {
				...monitor,
				id: monitor.id,
				width: (monitor.width * splitPoint) / 100,
			};

			const rightMonitor: Monitor = {
				id: `monitor-${monitorIdCounter}`,
				dpi: 100,
				x: monitor.x + (monitor.width * splitPoint) / 100,
				y: monitor.y,
				width: (monitor.width * (100 - splitPoint)) / 100,
				height: monitor.height,
				isPrimary: false,
				number: monitorIdCounter,
			};

			return [leftMonitor, rightMonitor];
		}
	};

	/**
	 * Calculate the dynamic top margin for a monitor based on actual header height.
	 * For primary monitors: height of full header (DPI dropdown + "Primary Monitor" label) + 2%.
	 * For other monitors: height of DPI dropdown only + 2%.
	 */
	const computeTopMargin = useCallback((monitor: Monitor): number => {
		// Always use the full header ref — for primary monitors it includes
		// DPI dropdown + "Primary Monitor" label; for non-primary just DPI dropdown
		const headerEl = monitorHeaderRefs.current[monitor.id];
		if (!headerEl) {
			return 20;
		}

		const monitorEl = headerEl.parentElement;
		if (!monitorEl) {
			return 20;
		}

		const headerRect = headerEl.getBoundingClientRect();
		const monitorRect = monitorEl.getBoundingClientRect();

		if (monitorRect.height <= 0) {
			return 20;
		}

		// Use header's bottom edge relative to monitor's top to account for padding
		const headerEndRelative = headerRect.bottom - monitorRect.top;
		const marginPercent = (headerEndRelative / monitorRect.height) * 100 + 2;
		return Math.max(marginPercent, 20);
	}, []);

	const handleCanvasClick = (e: React.MouseEvent) => {
		if (!canvasRef.current) return;

		const rect = canvasRef.current.getBoundingClientRect();
		const clickX = ((e.clientX - rect.left) / rect.width) * 100;
		const clickY = ((e.clientY - rect.top) / rect.height) * 100;

		const clickedMonitor = findClickedMonitor(clickX, clickY);

		if (clickedMonitor) {
			// Calculate relative position within the monitor
			const relativeX = clickX - clickedMonitor.x;
			const relativeY = clickY - clickedMonitor.y;
			const relativeXPercent = (relativeX / clickedMonitor.width) * 100;
			const relativeYPercent = (relativeY / clickedMonitor.height) * 100;

			// Check if click is within allowed range based on partition mode
			const marginPercent = 20;
			if (partitionMode === 'vertical') {
				// For vertical partition, check horizontal position (left to right)
				// Block left 20% and right 20%, allow middle 60%
				if (
					relativeXPercent <= marginPercent ||
					relativeXPercent >= 100 - marginPercent
				) {
					return; // Click is in the margin area, don't create partition
				}
			} else {
				// For horizontal partition, compute top margin from actual header height
				const topMargin = computeTopMargin(clickedMonitor);
				if (relativeYPercent <= topMargin || relativeYPercent >= 100 - marginPercent) {
					return; // Click is in the margin area, don't create partition
				}
			}

			const [monitor1, monitor2] = splitMonitor(
				clickedMonitor,
				clickX,
				clickY,
				partitionMode
			);

			setMonitors((prev) => {
				const filtered = prev.filter((m) => m.id !== clickedMonitor.id);
				const updated = [...filtered, monitor1, monitor2];
				return updated;
			});

			setMonitorIdCounter((prev) => prev + 1);
			setPreviewSplit(null);
		}
	};

	const handleMonitorClick = (e: React.MouseEvent, monitor: Monitor) => {
		if ((e.target as HTMLElement).closest('.monitor-dpi-selector')) {
			return;
		}

		setSelectedMonitor(monitor.id);
	};

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!canvasRef.current) return;

			const rect = canvasRef.current.getBoundingClientRect();
			const mouseX = ((e.clientX - rect.left) / rect.width) * 100;
			const mouseY = ((e.clientY - rect.top) / rect.height) * 100;
			const hoveredMon = findClickedMonitor(mouseX, mouseY);

			if (hoveredMon) {
				const relativeX = mouseX - hoveredMon.x;
				const relativeY = mouseY - hoveredMon.y;

				const marginPercent = 20;

				if (partitionMode === 'horizontal') {
					const splitPercentage = (relativeY / hoveredMon.height) * 100;
					const topMargin = computeTopMargin(hoveredMon);
					// Only show preview if not in margin area (dynamic top, fixed bottom)
					if (splitPercentage >= topMargin && splitPercentage <= 100 - marginPercent) {
						// Global position: relative to entire canvas top
						const globalPosition = mouseY;
						setPreviewSplit({
							monitorId: hoveredMon.id,
							position: splitPercentage,
							globalPosition,
						});
					} else {
						setPreviewSplit(null);
					}
				} else {
					const splitPercentage = (relativeX / hoveredMon.width) * 100;
					// Only show preview if not in margin area
					if (
						splitPercentage >= marginPercent &&
						splitPercentage <= 100 - marginPercent
					) {
						// Global position: relative to entire canvas left
						const globalPosition = mouseX;
						setPreviewSplit({
							monitorId: hoveredMon.id,
							position: splitPercentage,
							globalPosition,
						});
					} else {
						setPreviewSplit(null);
					}
				}
			} else {
				setPreviewSplit(null);
			}
		},
		[partitionMode, findClickedMonitor, computeTopMargin]
	);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (canvas) {
			const mouseMoveHandler = handleMouseMove as EventListener;
			canvas.addEventListener('mousemove', mouseMoveHandler);
			return () => {
				canvas.removeEventListener('mousemove', mouseMoveHandler);
			};
		}
	}, [handleMouseMove]);

	const handleReset = () => {
		const defaultMonitors: Monitor[] = [
			{
				id: 'monitor-1',
				dpi: 100,
				x: 0,
				y: 0,
				width: 100,
				height: 100,
				isPrimary: true,
				number: 1,
			},
		];
		setMonitors(defaultMonitors);
		setMonitorIdCounter(2);
		setSelectedMonitor(null);
		setPreviewSplit(null);
	};

	const handleDpiChange = (monitorId: string, newDpi: string) => {
		setMonitors((prev) =>
			prev.map((monitor) =>
				monitor.id === monitorId ? { ...monitor, dpi: parseInt(newDpi) } : monitor
			)
		);
	};

	const handlePrimaryMonitorChange = (monitorId: string) => {
		setMonitors((prev) =>
			prev.map((monitor) => ({
				...monitor,
				isPrimary: monitor.id === monitorId,
			}))
		);
	};

	const handleApply = () => {
		saveLayoutToBridge(monitors);
	};

	const renderMonitor = (monitor: Monitor) => {
		const style: React.CSSProperties = {
			left: `${monitor.x}%`,
			top: `${monitor.y}%`,
			width: `${monitor.width}%`,
			height: `${monitor.height}%`,
		};

		const isSelected = selectedMonitor === monitor.id;

		return (
			<div
				key={monitor.id}
				className={`monitor ${hoveredMonitor === monitor.id ? 'hovered' : ''} ${
					isSelected ? 'selected' : ''
				} ${monitor.isPrimary ? 'primary' : ''}`}
				style={style}
				onMouseEnter={() => setHoveredMonitor(monitor.id)}
				onMouseLeave={() => setHoveredMonitor(null)}
				onClick={(e) => handleMonitorClick(e, monitor)}
				onContextMenu={(e) => {
					e.preventDefault();
					handlePrimaryMonitorChange(monitor.id);
				}}
			>
				<div
					className="monitor-header"
					ref={(el) => {
						monitorHeaderRefs.current[monitor.id] = el;
					}}
					onMouseDown={(e) => e.stopPropagation()}
					onClick={(e) => e.stopPropagation()}
				>
					<div
						className="monitor-dpi-selector"
						ref={(el) => {
							monitorDpiRefs.current[monitor.id] = el;
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<span className="dpi-label">DPI:</span>
						<Dropdown
							options={dpiOptions}
							value={monitor.dpi.toString()}
							onChange={(value) => handleDpiChange(monitor.id, value)}
							showLabel={false}
							className="monitor-dpi-dropdown"
						/>
					</div>
				</div>
				{monitor.isPrimary && (
					<div className="primary-indicator">
						<span className="primary-label">Primary</span>
					</div>
				)}

				{previewSplit && previewSplit.monitorId === monitor.id && (
					<div
						className={`split-preview ${partitionMode}`}
						style={
							partitionMode === 'horizontal'
								? { top: `${previewSplit.position}%` }
								: { left: `${previewSplit.position}%` }
						}
					>
						<span className="split-preview-percentage">
							{Math.round(previewSplit.globalPosition)}%
						</span>
					</div>
				)}
			</div>
		);
	};

	return (
		<SettingCard
			title={title}
			description={description}
			learnMoreUrl={learnMoreUrl}
			learnMoreText={learnMoreText}
			settingId="monitor-layout"
			content={
				<div className="monitor-layout-container">
					<div
						ref={canvasRef}
						className="monitor-canvas"
						onClick={handleCanvasClick}
						onMouseLeave={() => setPreviewSplit(null)}
					>
						{monitors.map(renderMonitor)}
					</div>

					<div className="monitor-controls">
						<div className="partition-mode-radio">
							<RadioSetting
								settingId="partition-mode"
								title=""
								description=""
								card={false}
								selectedValue={partitionMode}
								onChange={(value) => setPartitionMode(value as PartitionMode)}
								options={[
									{ value: 'horizontal', label: 'Horizontal' },
									{ value: 'vertical', label: 'Vertical' },
								]}
							/>
						</div>

						<div className="action-buttons">
							<PrimaryButton onClick={handleApply} ariaLabel="Apply monitor layout">
								Apply
							</PrimaryButton>
							<SecondaryButton onClick={handleReset} ariaLabel="Reset monitor layout">
								Reset
							</SecondaryButton>
						</div>
					</div>
				</div>
			}
		/>
	);
};
